// カラーパレット
export const colors = {
  primary: '#09529f',
  primaryDark: '#002348',
  secondary: '#ff7e00',
  secondaryDark: '#eb6100',
  success: '#28a745',
  successLight: '#d4edda',
  successBorder: '#c3e6cb',
  successText: '#155724',
  error: '#dc3545',
  errorLight: '#f8d7da',
  errorBorder: '#f5c6cb',
  errorText: '#721c24',
  info: '#17a2b8',
  infoLight: '#d1ecf1',
  infoBorder: '#bee5eb',
  infoText: '#0c5460',
  required: '#e74c3c',
  white: '#fff',
  lightGray: '#f5f5f5',
  borderGray: '#ddd',
  textGray: '#666',
  tableGray: '#fafafa',
  tableBorder: '#dddddd',
  appBackground: '#F0F8FF'
};

// フォント設定
export const fonts = {
  main: "'Roboto', 'Noto Sans JP', sans-serif",
  inherit: 'inherit'
};

// 基本レイアウト
export const layout = {
  container: {
    maxWidth: '600px',
    width: '100%',
    margin: '0 auto',
    padding: '25px',
    background: colors.white,
    border: `3px solid ${colors.primary}`,
    borderRadius: '5px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    fontFamily: fonts.main
  },
  fullScreenCenter: {
    minHeight: '80vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '15px',
    backgroundColor: colors.appBackground
  }
};

// ボタンスタイル
export const buttons = {
  base: {
    width: '100%',
    padding: '15px 20px',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
    fontFamily: fonts.inherit,
    textDecoration: 'none',
    display: 'inline-block',
    textAlign: 'center',
    lineHeight: 'normal'
  },
  primary: {
    backgroundColor: colors.primary,
    color: colors.white,
    boxShadow: `0 3px 0 ${colors.primaryDark}`
  },
  primaryHover: {
    backgroundColor: '#008fc4',
    transform: 'translateY(-2px)',
    boxShadow: `0 5px 0 ${colors.primaryDark}`
  },
  secondary: {
    backgroundColor: colors.secondary,
    color: colors.white,
    boxShadow: `0 3px 0 ${colors.secondaryDark}`
  },
  disabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    backgroundColor: '#ccc',
    boxShadow: '0 3px 0 #999'
  },
  link: {
    color: colors.primary,
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: '14px',
    background: 'transparent',
    border: 'none',
    padding: '8px 16px',
    margin: '10px 0',
    fontFamily: fonts.inherit,
    fontWeight: 'normal',
    width: '100%',
    textAlign: 'center',
    transition: 'color 0.3s'
  }
};

// フォーム要素
export const forms = {
  input: {
    width: '100%',
    padding: '12px 15px',
    border: `1px solid ${colors.borderGray}`,
    borderRadius: '4px',
    fontSize: '16px',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s',
    fontFamily: fonts.inherit
  },
  inputFocus: {
    outline: 'none',
    borderColor: colors.primary,
    boxShadow: `0 0 5px rgba(9, 82, 159, 0.1)`
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    margin: '20px 0'
  },
  th: {
    border: `1px solid ${colors.tableBorder}`,
    padding: '15px',
    textAlign: 'left',
    fontWeight: '600',
    background: colors.tableGray,
    color: '#333',
    width: '200px',
    minWidth: '200px'
  },
  td: {
    border: `1px solid ${colors.tableBorder}`,
    padding: '15px'
  }
};

// メッセージスタイル
export const messages = {
  base: {
    padding: '12px',
    borderRadius: '4px',
    margin: '20px 0',
    textAlign: 'center',
    fontWeight: '500',
    animation: 'slideIn 0.3s ease'
  },
  success: {
    color: colors.successText,
    backgroundColor: colors.successLight,
    border: `1px solid ${colors.successBorder}`
  },
  error: {
    color: colors.errorText,
    backgroundColor: colors.errorLight,
    border: `1px solid ${colors.errorBorder}`
  },
  info: {
    color: colors.infoText,
    backgroundColor: colors.infoLight,
    border: `1px solid ${colors.infoBorder}`
  }
};

// プログレス要素
export const progress = {
  bar: {
    width: '100%',
    height: '4px',
    backgroundColor: '#e9ecef',
    borderRadius: '2px',
    marginBottom: '30px',
    overflow: 'hidden'
  },
  fill: {
    height: '100%',
    backgroundColor: colors.primary,
    transition: 'width 0.3s ease'
  }
};

// テキストスタイル
export const text = {
  helpText: {
    fontSize: '14px',
    color: colors.textGray,
    marginTop: '10px',
    lineHeight: '1.5'
  },
  required: {
    color: colors.required,
    fontWeight: 'bold'
  },
  timer: {
    textAlign: 'center',
    marginTop: '15px',
    fontSize: '14px',
    color: colors.textGray
  }
};

// アニメーション・エフェクト
export const effects = {
  loading: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid #ffffff',
    borderRadius: '50%',
    borderTopColor: 'transparent',
    animation: 'spin 1s ease-in-out infinite',
    marginRight: '10px'
  }
};

// ユーティリティ関数
export const utils = {
  // ボタンスタイルを組み合わせる
  combineButtonStyles: (baseStyle = 'primary', modifiers = []) => {
    const base = { ...buttons.base };
    
    switch (baseStyle) {
      case 'primary':
        Object.assign(base, buttons.primary);
        break;
      case 'secondary':
        Object.assign(base, buttons.secondary);
        break;
      case 'link':
        Object.assign(base, buttons.link);
        break;
      default:
        Object.assign(base, buttons.primary);
        break;
    }
    
    modifiers.forEach(modifier => {
      switch (modifier) {
        case 'disabled':
          Object.assign(base, buttons.disabled);
          break;
        case 'hover':
          Object.assign(base, buttons.primaryHover);
          break;
        default:
          break;
      }
    });
    
    return base;
  },
  
  // メッセージスタイルを取得
  getMessageStyle: (type = 'info') => {
    let typeStyle;
    switch (type) {
      case 'success':
        typeStyle = messages.success;
        break;
      case 'error':
        typeStyle = messages.error;
        break;
      default:
        typeStyle = messages.info;
        break;
    }
    return { ...messages.base, ...typeStyle };
  }
};